import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  RefreshCw,
  Mail,
  MailOpen,
  Archive,
  Trash2,
  Calendar,
  Inbox,
  Reply,
} from "lucide-react";
import {
  fetchContactMessages,
  updateContactMessageStatus,
  deleteContactMessage,
  CONTACT_STATUS_META,
} from "@/api/adminContact";

const TABS = [
  { key: "new", label: "Nieuw" },
  { key: "read", label: "Gelezen" },
  { key: "archived", label: "Gearchiveerd" },
];

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const meta = CONTACT_STATUS_META[status] || CONTACT_STATUS_META.new;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-[Poppins] font-semibold uppercase tracking-wider"
      style={{ backgroundColor: meta.color, color: meta.textColor }}
    >
      {meta.label}
    </span>
  );
};

const MessageCard = ({ msg, onChanged }) => {
  const [busy, setBusy] = useState("");
  const doUpdate = async (newStatus) => {
    setBusy(newStatus);
    try {
      await updateContactMessageStatus(msg.id, newStatus);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy("");
    }
  };
  const doDelete = async () => {
    if (!window.confirm("Dit bericht definitief verwijderen?")) return;
    setBusy("delete");
    try {
      await deleteContactMessage(msg.id);
      onChanged();
    } catch {
      /* ignore */
    } finally {
      setBusy("");
    }
  };
  const mailto = `mailto:${msg.email}?subject=${encodeURIComponent(
    msg.onderwerp ? `Re: ${msg.onderwerp}` : "Reactie via Lemmer Leeft",
  )}`;

  return (
    <article
      data-testid={`contact-message-${msg.id}`}
      className="bg-white rounded-2xl border border-[#E4EEF5] p-5 sm:p-6"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <StatusBadge status={msg.status || "new"} />
            <span className="inline-flex items-center gap-1 text-xs text-[#5A7594] font-[Inter]">
              <Calendar className="w-3 h-3" />
              {formatDate(msg.created_at)}
            </span>
          </div>
          <h3 className="font-[Poppins] font-bold text-[#173A5E] text-lg leading-tight">
            {msg.naam}
          </h3>
          <a
            href={`mailto:${msg.email}`}
            className="text-sm text-[#2E9CD6] hover:text-[#173A5E] transition font-[Inter] break-all"
          >
            {msg.email}
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <a
            href={mailto}
            data-testid={`btn-reply-${msg.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#2E9CD6] hover:bg-[#173A5E] text-white text-xs font-[Poppins] font-semibold transition"
          >
            <Reply className="w-3.5 h-3.5" />
            Reageer
          </a>
          {msg.status !== "read" && (
            <button
              onClick={() => doUpdate("read")}
              disabled={!!busy}
              data-testid={`btn-mark-read-${msg.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F4F8FB] hover:bg-[#E4EEF5] text-[#173A5E] text-xs font-[Poppins] font-semibold transition disabled:opacity-50"
            >
              {busy === "read" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <MailOpen className="w-3.5 h-3.5" />
              )}
              Markeer gelezen
            </button>
          )}
          {msg.status !== "new" && (
            <button
              onClick={() => doUpdate("new")}
              disabled={!!busy}
              data-testid={`btn-mark-new-${msg.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F4F8FB] hover:bg-[#E4EEF5] text-[#173A5E] text-xs font-[Poppins] font-semibold transition disabled:opacity-50"
            >
              {busy === "new" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Mail className="w-3.5 h-3.5" />
              )}
              Terug naar nieuw
            </button>
          )}
          {msg.status !== "archived" && (
            <button
              onClick={() => doUpdate("archived")}
              disabled={!!busy}
              data-testid={`btn-archive-${msg.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F4F8FB] hover:bg-[#E4EEF5] text-[#173A5E] text-xs font-[Poppins] font-semibold transition disabled:opacity-50"
            >
              {busy === "archived" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Archive className="w-3.5 h-3.5" />
              )}
              Archiveer
            </button>
          )}
          <button
            onClick={doDelete}
            disabled={!!busy}
            data-testid={`btn-delete-${msg.id}`}
            aria-label="Verwijderen"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#FDECEE] hover:bg-[#F5C3C8] text-[#8b1420] text-xs font-[Poppins] font-semibold transition disabled:opacity-50"
          >
            {busy === "delete" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>
      {msg.onderwerp && (
        <p className="text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1">
          {msg.onderwerp}
        </p>
      )}
      <p className="text-[#3E5871] font-[Inter] whitespace-pre-line leading-relaxed">
        {msg.bericht}
      </p>
    </article>
  );
};

export const AdminContactPage = () => {
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, read: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCounts = useCallback(async () => {
    try {
      const [n, r, a] = await Promise.all([
        fetchContactMessages("new"),
        fetchContactMessages("read"),
        fetchContactMessages("archived"),
      ]);
      setCounts({ new: n.length, read: r.length, archived: a.length });
    } catch {
      /* silent */
    }
  }, []);

  const load = useCallback(
    async (activeTab = tab) => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchContactMessages(activeTab);
        setItems(data);
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            "Kon berichten niet laden. Probeer opnieuw.",
        );
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [tab],
  );

  useEffect(() => {
    load(tab);
    loadCounts();
  }, [tab, load, loadCounts]);

  const handleChanged = useCallback(() => {
    load(tab);
    loadCounts();
  }, [tab, load, loadCounts]);

  return (
    <div data-testid="admin-contact-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#2E9CD6] mb-1">
            Contact-inbox
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold">Contactberichten</h1>
          <p className="mt-2 text-[#3E5871] font-[Inter] max-w-2xl">
            Alle inzendingen uit het contactformulier. Reageer direct via
            e-mail, of markeer als gelezen/archiveer.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            load(tab);
            loadCounts();
          }}
          data-testid="contact-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E4EEF5] hover:bg-[#F4F8FB] text-[#173A5E] font-[Poppins] font-semibold text-sm transition"
        >
          <RefreshCw className="w-4 h-4" />
          Verversen
        </button>
      </div>

      <div data-testid="contact-tabs" className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`contact-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-[Poppins] font-semibold transition border ${
                active
                  ? "bg-[#173A5E] text-white border-[#173A5E]"
                  : "bg-white text-[#173A5E] border-[#E4EEF5] hover:border-[#6BC0E8]"
              }`}
            >
              {t.label}
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold ${
                  active
                    ? "bg-white/25 text-white"
                    : "bg-[#F4F8FB] text-[#173A5E]"
                }`}
              >
                {counts[t.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#5A7594] py-8">
          <Loader2 className="w-4 h-4 animate-spin" />
          Laden…
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420]">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="contact-empty"
          className="text-center py-16 bg-white rounded-2xl border border-[#E4EEF5]"
        >
          <div className="w-14 h-14 rounded-full bg-[#F4F8FB] flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-6 h-6 text-[#2E9CD6]" />
          </div>
          <h3 className="text-xl font-[Poppins] font-bold text-[#173A5E] mb-2">
            Geen berichten in deze categorie
          </h3>
          <p className="text-[#5A7594] font-[Inter] max-w-sm mx-auto">
            {tab === "new"
              ? "Zodra iemand het contactformulier invult, verschijnt het bericht hier."
              : "Wanneer je berichten verwerkt, worden ze hier zichtbaar."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map((m) => (
            <MessageCard key={m.id} msg={m} onChanged={handleChanged} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminContactPage;
